import { get, post } from '../../lib/api/client';
import type { PersonRef } from '../dishes/api';
import { fromP1, toP1Query, type P1Response, type Page, type PageRequest } from '../../lib/api/paging';

/**
 * Eater feedback triage (BE `admin-content-feedback.go`, P1 paging): the queue,
 * one submission's detail, and triage (status + appended notes). Feedback is
 * an eater's review OF a chef; comments are admin-only, never shown to chefs.
 */
export type TriageStatus = 'new' | 'reviewed' | 'follow_up';
export const TRIAGE_STATUSES: TriageStatus[] = ['new', 'follow_up', 'reviewed'];

/** BE `feedbackOrder` allowlist; default is worst-rated first, then newest. */
export type FeedbackSortKey = 'rating' | 'submittedAt' | 'chef' | 'eater' | 'triageStatus';

/** `model` feedback row. */
export interface FeedbackRow {
  id: string;
  rating: number;
  note?: string;
  eaterProfileId: string;
  eaterDisplayName: string;
  chefProfileId: string;
  chefDisplayName: string;
  orderId: string;
  orderNumber: string;
  listingId: string;
  dishTitle: string;
  localeId?: string;
  submittedAt: string;
  triageStatus: TriageStatus;
  lowRating: boolean;
}

export interface FeedbackParams extends PageRequest {
  chefProfileId?: string;
  triageStatus?: TriageStatus;
  localeIds?: string[];
  ratingMin?: number;
  ratingMax?: number;
  /** YYYY-MM-DD, admin profile timezone, inclusive. */
  startDate?: string;
  endDate?: string;
  sortBy?: FeedbackSortKey;
  order?: 'asc' | 'desc';
}

export interface FeedbackPage extends Page<FeedbackRow> {
  /** Ratings at or below this are "low" (config `feedback.lowRatingThreshold`). */
  lowRatingThreshold: number;
}

export async function listFeedback(
  { page, pageSize, ...filters }: FeedbackParams,
  signal?: AbortSignal,
): Promise<FeedbackPage> {
  const res = await get<P1Response<FeedbackRow> & { lowRatingThreshold?: number }>(
    '/admin/feedback',
    { ...filters, order: filters.sortBy ? filters.order : undefined, ...toP1Query({ page, pageSize }) },
    signal,
  );
  return { ...fromP1(res), lowRatingThreshold: res.lowRatingThreshold ?? 0 };
}

export interface TriageNote {
  id: string;
  /** RFC 3339, UTC. */
  at: string;
  byProfileId: string;
  text: string;
}

export interface TriageState {
  status: TriageStatus;
  /** Last status change. */
  at?: string;
  byProfileId?: string;
  /** Accumulated, oldest first. */
  notes: TriageNote[];
}

/** `model.ListingReview` fields the dialog reads. */
export interface FeedbackReview {
  id: string;
  createdAt: string;
  rating: number;
  note?: string;
  orderId: string;
  listingId: string;
}

export interface FeedbackDetail {
  feedback: FeedbackReview;
  eater: PersonRef | null;
  chef: PersonRef | null;
  triage: TriageState;
  order: { id: string; orderNumber: string } | null;
  listing: { id: string; title: string } | null;
  /** Other feedback about the same chef, newest first (up to 50). */
  priorForChef: FeedbackRow[];
  priorCount: number;
}

export async function getFeedbackDetail(id: string, signal?: AbortSignal): Promise<FeedbackDetail> {
  const res = await get<FeedbackDetail>(`/admin/feedback/${encodeURIComponent(id)}`, undefined, signal);
  return { ...res, priorForChef: res.priorForChef ?? [], triage: { ...res.triage, notes: res.triage?.notes ?? [] } };
}

/** Sets the status; a note, when given, is appended to the history. */
export async function triageFeedback(id: string, status: TriageStatus, note?: string): Promise<TriageState> {
  const res = await post<{ id: string; triage: TriageState }>(`/admin/feedback/${encodeURIComponent(id)}/triage`, {
    status,
    note: note || undefined,
  });
  return res.triage;
}
